import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { Copy, Key, RefreshCw, X, Plus } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as adminService from "@/shared/api/services/adminService";

function generateApiKey() {
  return "pk_live_" + Array.from({ length: 32 }, () => 
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

function generateSecretKey() {
  return "sk_live_" + Array.from({ length: 40 }, () => 
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

export default function MerchantForm({ merchant, open, onClose, onSave, engines = [], resellers = [] }) {
  const { t } = useLanguage();
  const [currentTab, setCurrentTab] = useState("general");
  const [formData, setFormData] = useState(
    merchant 
      ? { 
          ...merchant,
          api_key: merchant.apiKey,
          secret_key: merchant.secretKey,
          callback_url: merchant.callbackUrl,
          webhook_url: merchant.webhookUrl,
        }
      : {
          name: "",
          email: "",
          password: "",
          status: "ACTIVE",
          parent_type: "direct",
          engine_id: "",
          reseller_id: "",
          api_key: generateApiKey(),
          secret_key: generateSecretKey(),
          callback_url: "",
          webhook_url: "",
          ip_whitelist: [],
          rate_limit: 100,
          deposit_enabled: true,
          withdrawal_enabled: true,
          min_deposit: 100,
          max_deposit: 1000000,
          min_withdrawal: 100,
          max_withdrawal: 500000,
          daily_deposit_limit: 10000000,
          daily_withdrawal_limit: 5000000,
          fee_type: "percentage",
          fee_value: 2.5,
          settlement_period: "T1",
          contact_name: "",
          contact_email: "",
          contact_phone: "",
          notes: "",
        }
  );
  const [newIp, setNewIp] = useState("");
  const [saving, setSaving] = useState(false);
  const [keysRegenerated, setKeysRegenerated] = useState(false);

  // Reset to first tab when dialog opens; sync form from merchant or create defaults
  useEffect(() => {
    if (open) {
      setCurrentTab("general");
      if (merchant) {
        // Edit: set api/secret from merchant (list often has none) so we don't show stale random keys
        setFormData((prev) => ({
          ...prev,
          ...merchant,
          api_key: merchant.apiKey ?? "",
          secret_key: merchant.secretKey ? "••••••••" : "",
          callback_url: merchant.callbackUrl ?? prev.callback_url ?? "",
          webhook_url: merchant.webhookUrl ?? prev.webhook_url ?? "",
          password: "",
          parent_type: merchant.createdByEngineId ? "engine" : merchant.createdByResellerId ? "reseller" : "direct",
          engine_id: merchant.createdByEngineId ?? "",
          reseller_id: merchant.createdByResellerId ?? "",
        }));
        setKeysRegenerated(false);
      } else {
        // Create: ensure fresh api_key and secret_key
        setFormData((prev) => ({
          ...prev,
          api_key: generateApiKey(),
          secret_key: generateSecretKey(),
        }));
      }
    }
  }, [open, merchant]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addIp = () => {
    if (newIp && !formData.ip_whitelist?.includes(newIp)) {
      handleChange("ip_whitelist", [...(formData.ip_whitelist || []), newIp]);
      setNewIp("");
    }
  };

  const removeIp = (ip) => {
    handleChange(
      "ip_whitelist",
      formData.ip_whitelist?.filter((i) => i !== ip)
    );
  };

  const regenerateKeys = () => {
    setKeysRegenerated(true);
    handleChange("api_key", generateApiKey());
    handleChange("secret_key", generateSecretKey());
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Prepare data for backend based on whether creating or updating
      let dataToSave;
      
      if (merchant) {
        // Update - omit apiKey/secretKey unless user clicked Regenerate
        dataToSave = {
          name: formData.name,
          status: formData.status,
          webhookUrl: formData.webhook_url,
          callbackUrl: formData.callback_url,
        };
        if (keysRegenerated) {
          dataToSave.apiKey = formData.api_key;
          dataToSave.secretKey = formData.secret_key;
        }

        // If password is provided, change it separately
        if (formData.password && formData.password.trim() !== "") {
          try {
            await adminService.changeMerchantPassword(merchant.id, formData.password);
          } catch (error) {
            console.error("Failed to change password:", error);
            toast.error("Failed to change password: " + (error.message || "Unknown error"));
            setSaving(false);
            return;
          }
        }
      } else {
        // Create - send required fields for creation
        dataToSave = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          apiKey: formData.api_key,
          secretKey: formData.secret_key,
          webhookUrl: formData.webhook_url || undefined,
          callbackUrl: formData.callback_url || undefined,
        };
        
        // Add parent reference based on parent_type
        if (formData.parent_type === "engine" && formData.engine_id) {
          dataToSave.engineId = formData.engine_id;
        } else if (formData.parent_type === "reseller" && formData.reseller_id) {
          dataToSave.resellerId = formData.reseller_id;
        }
        // If parent_type is "direct" or not specified, don't add engineId/resellerId
        // Backend will automatically set createdByAdminId
      }
      
      await onSave(dataToSave);
    } catch (error) {
      console.error("Failed to save merchant:", error);
      // Error is handled by global error handler
    } finally {
      setSaving(false);
    }
  };

  const handleNext = (nextTab) => {
    setCurrentTab(nextTab);
  };

  const tabOrder = ["general", "api", "limits", "contact"];
  const currentTabIndex = tabOrder.indexOf(currentTab);
  const isLastTab = currentTabIndex === tabOrder.length - 1;
  const isFirstTab = currentTabIndex === 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {merchant ? t("editMerchant") : t("addNewMerchant")}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={currentTab} onValueChange={setCurrentTab} className="mt-4">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="general">{t("general")}</TabsTrigger>
            <TabsTrigger value="api">{t("apiSettings")}</TabsTrigger>
            <TabsTrigger value="limits">{t("limitsFees")}</TabsTrigger>
            <TabsTrigger value="contact">{t("contact")}</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("merchantName")} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t("merchantNamePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("merchantCode")} *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                  placeholder={t("merchantCodePlaceholder")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("emailLabel")} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder={t("emailPlaceholder")}
                disabled={!!merchant}
                required
              />
              {merchant && <p className="text-xs text-slate-500">{t("emailCannotBeChanged")}</p>}
            </div>

            {!merchant ? (
              <div className="space-y-2">
                <Label>{t("password")} *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder={t("passwordPlaceholder")}
                  required
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{t("changePassword")}</Label>
                <Input
                  type="password"
                  value={formData.password || ""}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder={t("leaveBlankToKeepPassword")}
                />
                <p className="text-xs text-slate-500">{t("changePasswordDescription")}</p>
              </div>
            )}

            {merchant && (
              <div className="space-y-2">
                <Label>{t("status")}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => handleChange("status", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                    <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                    <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("parentType")}</Label>
              <Select
                value={formData.parent_type || "direct"}
                onValueChange={(v) => {
                  handleChange("parent_type", v);
                  handleChange("engine_id", "");
                  handleChange("reseller_id", "");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">{t("directAdmin")}</SelectItem>
                  <SelectItem value="engine">{t("engine")}</SelectItem>
                  <SelectItem value="reseller">{t("reseller")}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{t("whoManagesMerchant")}</p>
            </div>

            {formData.parent_type === "engine" && (
              <div className="space-y-2">
                <Label>{t("engine")}</Label>
                <Select
                  value={formData.engine_id || ""}
                  onValueChange={(v) => handleChange("engine_id", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectEngine")} />
                  </SelectTrigger>
                  <SelectContent>
                    {engines.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.email && `(${e.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.parent_type === "reseller" && (
              <div className="space-y-2">
                <Label>{t("reseller")}</Label>
                <Select
                  value={formData.reseller_id || ""}
                  onValueChange={(v) => handleChange("reseller_id", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectReseller")} />
                  </SelectTrigger>
                  <SelectContent>
                    {resellers.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} {r.email && `(${r.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>{t("enableDeposits")}</Label>
                <p className="text-sm text-slate-500">{t("allowDepositTransactions")}</p>
              </div>
              <Switch
                checked={formData.deposit_enabled}
                onCheckedChange={(v) => handleChange("deposit_enabled", v)}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>{t("enableWithdrawals")}</Label>
                <p className="text-sm text-slate-500">{t("allowWithdrawalTransactions")}</p>
              </div>
              <Switch
                checked={formData.withdrawal_enabled}
                onCheckedChange={(v) => handleChange("withdrawal_enabled", v)}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("notes")}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder={t("internalNotes")}
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="api" className="space-y-4 mt-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{t("security")}:</strong> {t("apiKeysShownOnce")}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("apiKeyPublic")}</Label>
                <Button variant="ghost" size="sm" onClick={regenerateKeys} className="gap-1">
                  <RefreshCw className="h-3 w-3" />
                  {t("regenerate")}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input placeholder={merchant && 'Leave blank to keep current API Key'} value={formData.api_key} readOnly className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.api_key)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("secretKey")}</Label>
              <div className="flex gap-2">
                <Input placeholder={merchant && 'Leave blank to keep current Secret Key'} value={formData.secret_key} readOnly className="font-mono text-sm" type="password" />
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.secret_key)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("callbackUrl")}</Label>
              <Input
                value={formData.callback_url}
                onChange={(e) => handleChange("callback_url", e.target.value)}
                placeholder={t("callbackUrlPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("webhookUrl")}</Label>
              <Input
                value={formData.webhook_url}
                onChange={(e) => handleChange("webhook_url", e.target.value)}
                placeholder={t("webhookUrlPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("ipWhitelist")}</Label>
              <div className="flex gap-2">
                <Input
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  placeholder={t("ipWhitelistPlaceholder")}
                />
                <Button onClick={addIp}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.ip_whitelist?.map((ip) => (
                  <Badge key={ip} variant="secondary" className="gap-1">
                    {ip}
                    <button onClick={() => removeIp(ip)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("rateLimit")}</Label>
              <Input
                type="number"
                value={formData.rate_limit}
                onChange={(e) => handleChange("rate_limit", parseInt(e.target.value))}
              />
            </div>
          </TabsContent>

          <TabsContent value="limits" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("minDepositThb")}</Label>
                <Input
                  type="number"
                  value={formData.min_deposit}
                  onChange={(e) => handleChange("min_deposit", parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("maxDepositThb")}</Label>
                <Input
                  type="number"
                  value={formData.max_deposit}
                  onChange={(e) => handleChange("max_deposit", parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("minWithdrawalThb")}</Label>
                <Input
                  type="number"
                  value={formData.min_withdrawal}
                  onChange={(e) => handleChange("min_withdrawal", parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("maxWithdrawalThb")}</Label>
                <Input
                  type="number"
                  value={formData.max_withdrawal}
                  onChange={(e) => handleChange("max_withdrawal", parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("dailyDepositLimitThb")}</Label>
                <Input
                  type="number"
                  value={formData.daily_deposit_limit}
                  onChange={(e) => handleChange("daily_deposit_limit", parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("dailyWithdrawalLimitThb")}</Label>
                <Input
                  type="number"
                  value={formData.daily_withdrawal_limit}
                  onChange={(e) => handleChange("daily_withdrawal_limit", parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("feeType")}</Label>
                <Select
                  value={formData.fee_type}
                  onValueChange={(v) => handleChange("fee_type", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t("percentage")}</SelectItem>
                    <SelectItem value="fixed">{t("fixed")}</SelectItem>
                    <SelectItem value="tiered">{t("tiered")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("feeValueLabel")} ({formData.fee_type === "percentage" ? "%" : "THB"})</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fee_value}
                  onChange={(e) => handleChange("fee_value", parseFloat(e.target.value))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("settlementPeriod")}</Label>
              <Select
                value={formData.settlement_period}
                onValueChange={(v) => handleChange("settlement_period", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="T0">{t("t0SameDay")}</SelectItem>
                  <SelectItem value="T1">{t("t1NextDay")}</SelectItem>
                  <SelectItem value="T3">{t("t3")}</SelectItem>
                  <SelectItem value="T7">{t("t7Weekly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          <TabsContent value="contact" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t("contactName")}</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => handleChange("contact_name", e.target.value)}
                placeholder={t("contactNamePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("contactEmail")}</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange("contact_email", e.target.value)}
                placeholder={t("contactEmailPlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("contactPhone")}</Label>
              <Input
                value={formData.contact_phone}
                onChange={(e) => handleChange("contact_phone", e.target.value)}
                placeholder={t("contactPhonePlaceholder")}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6 flex justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            {!isFirstTab && (
              <Button variant="outline" onClick={() => handleNext(tabOrder[currentTabIndex - 1])}>
                {t("backButton")}
              </Button>
            )}
          </div>
          <div>
            {!isLastTab ? (
              <Button onClick={() => handleNext(tabOrder[currentTabIndex + 1])}>
                {t("nextButton")}
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? t("saving") : merchant ? t("updateMerchant") : t("createMerchantBtn")}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}